import { isClient, logger } from '~/modules/utilities/cross_env_utils';

export const ERROR_CODES = {
    GENERIC_ISSUE: 'GENERIC_ISSUE',
    PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
    MISSING_ROUTE_PARAM: 'MISSING_ROUTE_PARAM',
    MISSING_QUERY_PARAM: 'MISSING_QUERY_PARAM',
    INVALID_QUERY_PARAM: 'INVALID_QUERY_PARAM',
    INVALID_STATE: 'INVALID_STATE',
    INVALID_LISTING: 'INVALID_LISTING',
    MISSING_LISTING: 'MISSING_LISTING',
};

/**
 * IMPORTANT: Be careful not to send any PII through any of these parameters.
 */
export function handleException(exception: unknown, context: string | Record<string, unknown>, debugData?: AnyObject) {
    const formattedContext = typeof context === 'object' ? formatContext(context) : context;

    // Client-Side Logging
    if (isClient) {
        logClientException(exception, formattedContext, debugData);
    }
    // Server-Side Logging
    else {
        // Check if logger from cross_env_utils has a custom handleServerException function
        if (typeof logger.handleServerException === 'function') {
            // Call the custom server-side exception handler
            logger.handleServerException(exception, formattedContext, debugData);
        } else {
            // Fallback logic if custom server-side handler is not set up
            console.error("Custom 'handleServerException' not found. Falling back to default server logging.");
            logClientException(exception, formattedContext, debugData);
        }
    }
}

// Client-side exception logging logic
function logClientException(exception: unknown, context: string, debugData?: AnyObject) {
    // Doing an 'as any' typecast is dangerous, do not use it unless you know what you're doing.
    // Here the danger is neutralized by using the optional chaining & with nullish coalescing operators.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dangerousUntypedError = exception as any;
    const enhancedData = {
        code: dangerousUntypedError?.code,
        status: dangerousUntypedError?.response?.status ?? -1,
        response: dangerousUntypedError?.response?.data,
        ...debugData,
    };

    if (exception instanceof Error) {
        logger.error(
            `[${context}] Error: Name=${exception.name}, Message=${exception.message}, DebugData=${JSON.stringify(
                enhancedData,
            )}${exception.stack ? `, Stack=${JSON.stringify(exception.stack)}` : ''}`,
        );
    } else {
        logger.error(
            `[${context}] Exception: Error=${JSON.stringify(exception)}, DebugData=${JSON.stringify(enhancedData)}`,
        );
    }
}

// Formats the context object into a readable string
export function formatContext(contextObj: Record<string, unknown>): string {
    return `(${Object.entries(contextObj)
        .map(([key, value]) => `${key}=${value}`)
        .join(', ')})`;
}
